import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  ReactNode,
} from "react";
import { useZero } from "@rocicorp/zero/react";
import { useAuth } from "@workos-inc/authkit-react";
import { postSystemLog } from "./system-log";
import { queries } from "./queries";
import { mutators } from "./mutators";
import { completePlatformSignOut } from "./logout";
import { API_URL } from "./config";
import {
  identifyPostHogUser,
  resetPostHog,
  resetPostHogGroups,
} from "./posthog";
import { identifySentryUser, resetSentryUser } from "./sentry";
import { postUserBootstrapRequest } from "@ano/shared/api/user-bootstrap-write";
import { detectHomeRegion } from "./home-region-detect";
import { CurrentUserContext } from "./current-user-context";
import type { CurrentUser } from "./current-user-context";

export type { CurrentUser };
export { CurrentUserContext };

/**
 * Derive a human-readable display name from an email address.
 * "ruben+anna@neuroshell.dev" → "Anna"
 * "jane.doe@example.com"      → "Jane Doe"
 * "ruben@neuroshell.dev"      → "Ruben"
 */
function formatEmailAsName(email: string): string {
  const local = email.split("@")[0];
  // If there's a +suffix, use the suffix as the name (sub-addressing)
  const part = local.includes("+") ? local.split("+").pop()! : local;
  // Split on dots, hyphens, underscores
  return part
    .split(/[.\-_]/)
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase())
    .join(" ");
}

const CURRENT_USER_SYNC_TIMEOUT_MS = 12_000;
const BOOTSTRAP_MAX_RETRIES = 3;
const BOOTSTRAP_RETRY_DELAY_MS = 2_000;

export function CurrentUserProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [loggingOut, setLoggingOut] = useState(false);
  const zero = useZero();
  const {
    user: workosUser,
    signOut,
    getAccessToken,
    isLoading: authLoading,
  } = useAuth();
  const workosUserId = workosUser?.id ?? null;
  const workosUserEmail = workosUser?.email ?? null;
  const workosUserFirstName = workosUser?.firstName ?? null;
  const workosUserLastName = workosUser?.lastName ?? null;
  const getAccessTokenRef = useRef(getAccessToken);
  const signOutRef = useRef(signOut);
  const bootstrappedUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    getAccessTokenRef.current = getAccessToken;
    signOutRef.current = signOut;
  }, [getAccessToken, signOut]);

  // Sync WorkOS user to Zero database
  useEffect(() => {
    let cancelled = false;
    const activeWorkosUserId = workosUserId;
    const activeWorkosUserEmail = workosUserEmail;

    if (!activeWorkosUserId || !activeWorkosUserEmail) {
      if (authLoading) {
        return;
      }

      setUser(null);
      setLoggingOut(false);
      bootstrappedUserIdRef.current = null;

      setLoading(false);
      return;
    }
    const resolvedWorkosUserId: string = activeWorkosUserId;
    const resolvedWorkosUserEmail: string = activeWorkosUserEmail;
    const alreadyBootstrapped =
      bootstrappedUserIdRef.current === resolvedWorkosUserId;

    if (!alreadyBootstrapped) {
      setLoading(true);
    }

    async function syncUser() {
      const workosId = resolvedWorkosUserId;
      const displayName =
        [workosUserFirstName, workosUserLastName].filter(Boolean).join(" ") ||
        formatEmailAsName(resolvedWorkosUserEmail);
      const email = resolvedWorkosUserEmail;

      // Avoid loading the full users table during auth bootstrap.
      const existingByEmail = await zero.run(queries.users.byEmail(email));
      const id = existingByEmail?.id ?? workosId;

      const existing = await zero.run(queries.users.byId(id));
      if (existing) {
        // Block deactivated users from logging in
        if (existing.is_deactivated) {
          throw new Error("ACCOUNT_DEACTIVATED");
        }

        // hasDisplayName gates whether we overwrite the row's
        // display_name with the WorkOS-derived value. We treat the
        // following shapes as "missing" so they self-heal:
        //   - empty / whitespace
        //   - contains "@" (legacy email-fallback row)
        //   - equals the user's own id (cross-region stub from
        //     `/internal/workspaces/bootstrap-regional` — when an
        //     EU-home user is referenced in US PG before they've ever
        //     opened a US workspace, the stub writes display_name =
        //     user_id to satisfy NOT NULL. v0.1.45: detect and heal on
        //     visit so the row picks up the real name once the
        //     stub-user actually opens the cross-region workspace).
        const trimmed =
          typeof existing.display_name === "string"
            ? existing.display_name.trim()
            : "";
        const hasDisplayName =
          trimmed.length > 0 && !trimmed.includes("@") && trimmed !== id;
        await zero.mutate(
          mutators.users.update({
            id,
            email,
            ...(hasDisplayName ? {} : { display_name: displayName }),
            updated_at: Date.now(),
          }),
        );
        // Mark online via the dedicated presence mutator (Phase 1.5).
        await zero.mutate(mutators.user_presence.setOnline({ user_id: id }));
      } else {
        // Option F.3 (2026-05-20): direct regional `POST /api/users/bootstrap`
        // replaces the legacy CF-Worker chain
        //   client → POST /cp/users/init → regional /internal/users/bootstrap-regional
        // with one regional call. The endpoint upserts the `users` row
        // and (ON CONFLICT DO NOTHING) inserts the paired `user_presence`
        // row in one regional transaction; idempotent on retry.
        //
        // home_region is client-derived from the IANA timezone — same
        // signal the Worker used (stable across VPN / travel / airport
        // WiFi where IP-based geoip misroutes). No IP fallback: we
        // accept a misroute (US fallback) because the US authority
        // serves every workspace. The server stores `home_region` only
        // on initial INSERT and does NOT overwrite it on subsequent
        // bootstraps (see server/routes/user-bootstrap-write-routes.ts
        // ON CONFLICT clause) — so this value is load-bearing only on
        // first signup; it's ignored on every subsequent sign-in.
        const token = await getAccessTokenRef.current();
        if (!token) {
          throw new Error(
            "Cannot bootstrap user via /api/users/bootstrap: no access token",
          );
        }
        // IANA timezone for `users.timezone` (optional column). Capped
        // at 64 chars to match the regional handler's validation. The
        // longest current IANA zone is 32 chars; 64 is the safety
        // margin already encoded in the contract schema.
        let timezone: string | undefined;
        try {
          const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
          if (typeof tz === "string" && tz.length > 0 && tz.length <= 64) {
            timezone = tz;
          }
        } catch {
          // Non-fatal — endpoint accepts missing timezone.
        }
        // Cap display_name to match the regional `users.display_name`
        // column. Without this, Google signups with long names would
        // 400 at the endpoint.
        const trimmedDisplayName = displayName.trim().slice(0, 100);
        await postUserBootstrapRequest({
          apiBaseUrl: API_URL,
          accessToken: token,
          body: {
            user_id: id,
            email: resolvedWorkosUserEmail,
            home_region: detectHomeRegion(),
            ...(timezone ? { timezone } : {}),
            ...(trimmedDisplayName ? { display_name: trimmedDisplayName } : {}),
          },
        });
        // The endpoint inserts `user_presence` with ON CONFLICT DO
        // NOTHING — guarantees the row exists but doesn't flip the
        // presence bit. Mirror the existing-user branch and mark the
        // user online via the dedicated mutator so the new user shows
        // online immediately.
        await zero.mutate(mutators.user_presence.setOnline({ user_id: id }));
      }

      return { id, display_name: displayName, email } satisfies CurrentUser;
    }

    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let retryTimeoutId: ReturnType<typeof setTimeout> | null = null;

    async function attemptBootstrap(attempt: number): Promise<void> {
      if (cancelled) return;

      const timeoutPromise = new Promise<never>((_, reject) => {
        timeoutId = setTimeout(() => {
          reject(new Error("Current user bootstrap timed out"));
        }, CURRENT_USER_SYNC_TIMEOUT_MS);
      });

      try {
        const nextUser = await Promise.race([syncUser(), timeoutPromise]);
        if (cancelled) return;
        setUser(nextUser);
        bootstrappedUserIdRef.current = nextUser.id;
        // Drop any anonymous-lurker community group bound by setPostHogGroup on
        // a public page BEFORE the person-stitch, so this user's authenticated
        // events aren't misattributed to a community they merely viewed.
        resetPostHogGroups();
        identifyPostHogUser({
          id: nextUser.id,
          email: nextUser.email,
          name: nextUser.display_name,
        });
        identifySentryUser({
          id: nextUser.id,
          email: nextUser.email,
          name: nextUser.display_name,
        });
        postSystemLog(`${nextUser.display_name} connected`, nextUser.id);

        // Auto-join workspaces by email domain (e.g., @ano.dev → join Ano workspace).
        // Users with no matching domain land in Home and can create/join workspaces manually.
        void (async () => {
          try {
            const token = await getAccessTokenRef.current();
            if (token) {
              await fetch(`${API_URL}/api/workspace-auto-join`, {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${token}`,
                },
              });
            }
          } catch (err) {
            console.warn("workspace-auto-join failed:", err);
          }
        })();
      } catch (err) {
        if (cancelled) return;
        if (timeoutId) clearTimeout(timeoutId);
        timeoutId = null;

        // Don't retry if the account has been deactivated — sign out immediately
        if (err instanceof Error && err.message === "ACCOUNT_DEACTIVATED") {
          console.warn("Account deactivated, signing out");
          setUser(null);
          bootstrappedUserIdRef.current = null;
          setLoading(false);
          void completePlatformSignOut(signOutRef.current);
          return;
        }

        if (attempt < BOOTSTRAP_MAX_RETRIES) {
          console.warn(
            `User bootstrap attempt ${attempt}/${BOOTSTRAP_MAX_RETRIES} failed, retrying...`,
            err,
          );
          await new Promise<void>((resolve) => {
            retryTimeoutId = setTimeout(resolve, BOOTSTRAP_RETRY_DELAY_MS);
          });
          return attemptBootstrap(attempt + 1);
        }
        console.error("User bootstrap failed after retries:", err);
        bootstrappedUserIdRef.current = null;
        setUser(null);
      } finally {
        if (timeoutId) clearTimeout(timeoutId);
        timeoutId = null;
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    void attemptBootstrap(1);

    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
      if (retryTimeoutId) clearTimeout(retryTimeoutId);
    };
  }, [
    workosUserId,
    workosUserEmail,
    workosUserFirstName,
    workosUserLastName,
    authLoading,
    zero,
  ]);

  const logout = useCallback(async () => {
    if (user) {
      postSystemLog(`${user.display_name} logged out`, user.id);
      zero.mutate(mutators.user_presence.setOffline({ user_id: user.id }));
    }
    setLoggingOut(true);
    setUser(null);
    resetPostHog();
    resetSentryUser();
    await completePlatformSignOut(signOutRef.current);
  }, [user, zero]);

  // Presence tracking — debounce visibility changes to avoid flicker on
  // rapid tab switches (e.g. Cmd+Tab back and forth).
  const visibilityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!user) return;

    const VISIBILITY_DEBOUNCE_MS = 2000;

    const handleVisibility = () => {
      if (visibilityTimerRef.current) {
        clearTimeout(visibilityTimerRef.current);
      }
      if (document.visibilityState === "visible") {
        // Come back online immediately
        zero.mutate(mutators.user_presence.setOnline({ user_id: user.id }));
      } else {
        // Delay going offline to avoid flicker on brief tab switches
        visibilityTimerRef.current = setTimeout(() => {
          visibilityTimerRef.current = null;
          zero.mutate(mutators.user_presence.setOffline({ user_id: user.id }));
        }, VISIBILITY_DEBOUNCE_MS);
      }
    };

    const handleBeforeUnload = () => {
      if (visibilityTimerRef.current) {
        clearTimeout(visibilityTimerRef.current);
      }
      try {
        zero.mutate(mutators.user_presence.setOffline({ user_id: user.id }));
      } catch {
        // Page is unloading — Zero may already be torn down (e.g. logout
        // races with close, HMR boundary, or a renderer crash). Nothing
        // useful to do here, and throwing would just lose the unload.
      }
    };

    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      if (visibilityTimerRef.current) {
        clearTimeout(visibilityTimerRef.current);
      }
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [user, zero]);

  const contextValue = useMemo(
    () => ({ user, logout, loading, loggingOut }),
    [user, logout, loading, loggingOut],
  );

  return (
    <CurrentUserContext.Provider value={contextValue}>
      {children}
    </CurrentUserContext.Provider>
  );
}

export function useCurrentUser() {
  return useContext(CurrentUserContext);
}
